import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { Configure, InstantSearch } from 'react-instantsearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { useDispatch, useSelector } from 'react-redux';
import { changeRowsPerPage } from '@/features/table/dealsTableSlice';
import { TableRefresh } from '@/components/table/TableRefresh';
import { useObjectContext } from '@/hooks/useObjectContext';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import { dealHeadCells, claimHeadCells } from '../DealTable/data';
import { debtorHeadCells } from '@/components/tables/DebtorsTable';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { AddNewDealButton } from '@/components/buttons/AddNewDealButton';
import DealTableFilters from '@/components/tables/DealTable/DealTableFilters';
import { getClientIdFilters } from '@/features/clients/helper';

export const ClientDealTable = ({ pipeline }) => {
	const dispatch = useDispatch();

	const { clientRow } = useObjectContext();

	const isClaims = pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isDebtors = pipeline == process.env.REACT_APP_PIPELINE_DEBTORS;

	const objectType = isClaims ? 'claim' : isDebtors ? 'debtor' : 'deal';

	const { getFeatureFlags } = useFeatureFlagContext();
	const automatedDebtors = Boolean(getFeatureFlags('automatedDebtors')?.active);

	const { rowsPerPage } = useSelector((state) => state.dealTable);

	const columns = useMemo(() => {
		const headCells = (isClaims ? claimHeadCells : isDebtors ? debtorHeadCells : dealHeadCells)
			.slice()
			.filter((c) => c.id !== 'clientName' && c.id !== 'contactName')
			.filter((c) => automatedDebtors || c.id !== 'automationEnabled');
		return parseHeadCells(headCells);
	}, [isClaims, isDebtors, automatedDebtors]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const clientFilter = getClientIdFilters(clientRow);

	const filterSchema = useMemo(() => {
		const pipelineFilter = !pipeline
			? `pipeline:![${process.env.REACT_APP_PIPELINE_CLAIMS},${process.env.REACT_APP_PIPELINE_DEBTORS}]`
			: `pipeline := ${pipeline}`;
		const schema = {
			filters: [clientFilter, pipelineFilter].join(' && '),
		};
		return schema;
	}, [pipeline, clientFilter]);

	return (
		<InstantSearch indexName='deals' searchClient={dealSearch}>
			<Configure {...filterSchema} />
			<TableRefresh table='dealTable' />
			<Stack direction='row' sx={{ alignItems: 'end' }}>
				<DealTableFilters {...{ pipeline, spacing: 1 }} />
				<Box sx={{ mb: 2.25, ml: 1 }}>
					<AddNewDealButton pipeline={pipeline} />
				</Box>
			</Stack>

			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType={'deal'}
				includeTasks={isClaims || isDebtors}
				columns={columns}
				includePolicies={isDebtors || columns.some((c) => c.field === 'policies')}
				includeTransactions={isDebtors}
				variant={`client${objectType}s`}
				showPreview={false}
				enableExport={false}
				enableColumnReorder
			/>
		</InstantSearch>
	);
};
ClientDealTable.displayName = 'ClientDealTable';
